import { useBackend } from '@cdab/scania/qpr/contexts/backend-provider'
import { downloadFileFromArrayBuffer } from '@cdab/utils'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { hasStatus } from './utils'

export type GetStatisticsReportState =
  | {
      status: 'success'
      message: string
    }
  | {
      status: 'error'
      message: string
    }
  | {
      status: 'loading'
      message: null
    }
  | {
      status: 'none'
      message: null
    }

export function useGetStatisticsReport(
  startDate: string,
  endDate: string,
  dosVersion: number
) {
  const { t } = useTranslation('reports')
  const [submitState, setSubmitState] = useState<GetStatisticsReportState>({
    status: 'none',
    message: null
  })
  const client = useBackend()

  const handleClickGetStatisticsReport = useCallback(async () => {
    setSubmitState({
      status: 'loading',
      message: null
    })

    try {
      const response = await client.ReportsService.GetStatisticsReport(
        startDate,
        endDate,
        dosVersion
      )

      const fileName = `StatisticsReport_${startDate}_to_${endDate}.xlsx`

      const downloadFile = downloadFileFromArrayBuffer(
        response.data,
        response.headers['content-type'] ?? '',
        fileName
      )

      if (response.status === 204 || !downloadFile) {
        setSubmitState({
          status: 'error',
          message: t('message.empty-report')
        })
        return
      }

      setSubmitState({
        status: 'success',
        message: t('message.success')
      })
    } catch (e) {
      if (hasStatus(e)) {
        if (e.status === 400) {
          setSubmitState({
            status: 'error',
            message: t('message.no-dealer-flags')
          })
          return
        }
      }

      setSubmitState({
        status: 'error',
        message: t('message.error')
      })
    }
  }, [client.ReportsService, startDate, endDate, dosVersion, t])

  return [submitState, handleClickGetStatisticsReport, setSubmitState] as const
}
